import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  alpha,
} from "@mui/material";
import {
  Star as StarIcon,
  Check as CheckIcon,
  Diamond as DiamondIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

export interface SubscriptionPlanType {
  id: "free" | "paid";
  name: string;
  price: string;
  description: string;
  features: string[];
  popular?: boolean;
}

interface SubscriptionPlanProps {
  selectedPlan: SubscriptionPlanType["id"];
  onPlanSelect: (planId: SubscriptionPlanType["id"]) => void;
}

/**
 * Temporary subscription plan component for onboarding flow.
 * Will be updated with proper Stripe integration and real pricing.
 *
 * @todo Update with Stripe integration for actual payment processing
 * @todo Connect to real subscription plans and pricing from backend
 */
export const SubscriptionPlan: React.FC<SubscriptionPlanProps> = ({
  selectedPlan,
  onPlanSelect,
}) => {
  const { t } = useTranslation();

  const plans: SubscriptionPlanType[] = [
    {
      id: "free",
      name: t(messages.Free_Plan()),
      price: t(messages.Free()),
      description: t(messages.Perfect_for_getting_started()),
      features: [
        t(messages.Basic_store_setup()),
        t(messages.Up_to_50_products()),
        t(messages.Standard_analytics()),
        t(messages.Email_support()),
        t(messages.Basic_customization()),
      ],
    },
    {
      id: "paid",
      name: t(messages.Pro_Plan()),
      price: "$29",
      description: t(messages.Advanced_features_for_growing_businesses()),
      features: [
        t(messages.Everything_in_Free()),
        t(messages.Unlimited_products()),
        t(messages.Advanced_analytics()),
        t(messages.Priority_support()),
        t(messages.Custom_branding()),
        t(messages.Advanced_integrations()),
        t(messages.Marketing_tools()),
      ],
      popular: true,
    },
  ];
  return (
    <Card
      elevation={1}
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {" "}
        <Box display="flex" alignItems="center" mb={3}>
          <StarIcon sx={{ color: "#4caf50", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t(messages.Choose_Your_Plan())}
          </Typography>
        </Box>
        <Grid container spacing={3}>
          {plans.map((plan) => (
            <Grid item xs={12} md={6} key={plan.id} sx={{ display: "flex" }}>
              <Card
                sx={{
                  position: "relative",
                  border:
                    selectedPlan === plan.id
                      ? "2px solid #4caf50"
                      : "1px solid",
                  borderColor:
                    selectedPlan === plan.id ? "#4caf50" : alpha("#000", 0.12),
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                  "&:hover": {
                    borderColor:
                      selectedPlan === plan.id ? "#4caf50" : "#02b7cf",
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  },
                }}
                onClick={() => onPlanSelect(plan.id)}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 16,
                      backgroundColor: "#f58321",
                      color: "white",
                      px: 2,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                    }}
                  >
                    {t(messages.POPULAR())}
                  </Box>
                )}

                <CardContent
                  sx={{
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    flex: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" mb={2}>
                    {plan.id === "free" ? (
                      <CheckIcon sx={{ color: "#4caf50", mr: 1 }} />
                    ) : (
                      <DiamondIcon sx={{ color: "#f58321", mr: 1 }} />
                    )}
                    <Typography variant="h6" fontWeight={600}>
                      {plan.name}
                    </Typography>
                  </Box>
                  <Box mb={2}>
                    <Typography
                      variant="h4"
                      component="span"
                      fontWeight={700}
                      color={plan.id === "free" ? "#4caf50" : "#f58321"}
                    >
                      {plan.price}
                    </Typography>{" "}
                    {plan.id === "paid" && (
                      <Typography
                        variant="body2"
                        component="span"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        {t(messages.month())}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    {plan.description}
                  </Typography>
                  <List dense sx={{ mb: 2, flex: 1 }}>
                    {plan.features.map((feature, index) => (
                      <ListItem key={index} disablePadding>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon
                            sx={{
                              fontSize: 16,
                              color: plan.id === "free" ? "#4caf50" : "#f58321",
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: "0.875rem",
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>{" "}
                  <Button
                    variant={
                      selectedPlan === plan.id ? "contained" : "outlined"
                    }
                    fullWidth
                    sx={{
                      borderColor: plan.id === "free" ? "#4caf50" : "#f58321",
                      color:
                        selectedPlan === plan.id
                          ? "white"
                          : plan.id === "free"
                          ? "#4caf50"
                          : "#f58321",
                      backgroundColor:
                        selectedPlan === plan.id
                          ? plan.id === "free"
                            ? "#4caf50"
                            : "#f58321"
                          : "transparent",
                      "&:hover": {
                        borderColor: plan.id === "free" ? "#43a047" : "#e57100",
                        backgroundColor:
                          selectedPlan === plan.id
                            ? plan.id === "free"
                              ? "#43a047"
                              : "#e57100"
                            : alpha(
                                plan.id === "free" ? "#4caf50" : "#f58321",
                                0.05
                              ),
                      },
                    }}
                  >
                    {selectedPlan === plan.id
                      ? t(messages.Selected())
                      : t(messages.Select_Plan())}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {selectedPlan && (
          <Box
            sx={{
              mt: 3,
              p: 2,
              backgroundColor: alpha("#4caf50", 0.05),
              borderRadius: 2,
              border: "1px solid",
              borderColor: alpha("#4caf50", 0.2),
            }}
          >
            {" "}
            <Typography variant="body2" color="#2e7d32">
              <strong>{t(messages.Selected())}:</strong>{" "}
              {plans.find((p) => p.id === selectedPlan)?.name}
              {selectedPlan === "free" &&
                ` - ${t(messages.You_can_upgrade_anytime_after_setup())}`}
              {selectedPlan === "paid" &&
                ` - ${t(messages.day_free_trial_included())}`}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
